import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FONT_MONO } from "../../theme/v2Tokens";

interface PaginationProps {
  page: number;
  totalPages: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

export default function Pagination({
  page,
  totalPages,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}: PaginationProps) {
  const { t } = useTranslation("common");
  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalCount);

  // Build page number buttons: show up to 7 buttons with ellipses
  const getPageButtons = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | "...")[] = [];
    if (page <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    } else if (page >= totalPages - 3) {
      pages.push(1);
      pages.push("...");
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      pages.push(page - 1);
      pages.push(page);
      pages.push(page + 1);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const pageButtons = getPageButtons();

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 30,
    height: 30,
    px: "6px",
    fontSize: "0.8125rem",
    fontFamily: FONT_MONO,
    fontWeight: 500,
    border: "1px solid var(--border-mid)",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background-color 150ms ease, border-color 150ms ease",
    userSelect: "none" as const,
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: 2,
        py: 1,
        backgroundColor: "var(--bg-surface)",
        borderTop: "1px solid var(--border-subtle)",
        flexWrap: "nowrap",
        minHeight: 48,
      }}
    >
      {/* Showing X–Y of Z */}
      <Box
        component="span"
        sx={{
          fontSize: "0.8125rem",
          fontFamily: FONT_MONO,
          color: "var(--text-secondary)",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {totalCount === 0
          ? t("showingZeroResults")
          : t("showingResults", { startItem, endItem, totalCount: totalCount.toLocaleString() })}
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Rows per page select */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "var(--text-tertiary)",
            whiteSpace: "nowrap",
          }}
        >
          {t("rowsPerPage")}
        </Box>
        <Box
          component="select"
          value={rowsPerPage}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onRowsPerPageChange(Number(e.target.value))
          }
          sx={{
            fontSize: "0.8125rem",
            fontFamily: FONT_MONO,
            color: "var(--text-primary)",
            backgroundColor: "var(--bg-raised)",
            border: "1px solid var(--border-mid)",
            borderRadius: "6px",
            px: "8px",
            py: "4px",
            cursor: "pointer",
            outline: "none",
            "&:focus": {
              borderColor: "var(--border-strong)",
            },
          }}
        >
          <option value={100}>100</option>
          <option value={500}>500</option>
          <option value={1000}>1000</option>
        </Box>
      </Box>

      {/* Prev button */}
      <Box
        component="button"
        onClick={() => page > 1 && onPageChange(page - 1)}
        disabled={page <= 1}
        sx={{
          ...btnBase,
          color: page <= 1 ? "var(--text-muted)" : "var(--text-secondary)",
          backgroundColor: "transparent",
          borderColor: "var(--border-mid)",
          cursor: page <= 1 ? "not-allowed" : "pointer",
          "&:hover": page > 1
            ? {
                backgroundColor: "var(--bg-hover)",
                borderColor: "var(--border-strong)",
                color: "var(--text-primary)",
              }
            : {},
        }}
        aria-label={t("previous")}
      >
        ‹
      </Box>

      {/* Page number buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {pageButtons.map((p, idx) =>
          p === "..." ? (
            <Box
              key={`ellipsis-${idx}`}
              component="span"
              sx={{
                fontSize: "0.8125rem",
                fontFamily: FONT_MONO,
                color: "var(--text-muted)",
                px: "4px",
              }}
            >
              …
            </Box>
          ) : (
            <Box
              key={p}
              component="button"
              onClick={() => onPageChange(p as number)}
              sx={{
                ...btnBase,
                color: page === p ? "var(--bg-deep)" : "var(--text-secondary)",
                backgroundColor:
                  page === p ? "var(--accent)" : "transparent",
                borderColor:
                  page === p ? "var(--accent)" : "var(--border-mid)",
                "&:hover":
                  page !== p
                    ? {
                        backgroundColor: "var(--bg-hover)",
                        borderColor: "var(--border-strong)",
                        color: "var(--text-primary)",
                      }
                    : {},
              }}
            >
              {p}
            </Box>
          )
        )}
      </Box>

      {/* Next button */}
      <Box
        component="button"
        onClick={() => page < totalPages && onPageChange(page + 1)}
        disabled={page >= totalPages}
        sx={{
          ...btnBase,
          color:
            page >= totalPages ? "var(--text-muted)" : "var(--text-secondary)",
          backgroundColor: "transparent",
          borderColor: "var(--border-mid)",
          cursor: page >= totalPages ? "not-allowed" : "pointer",
          "&:hover":
            page < totalPages
              ? {
                  backgroundColor: "var(--bg-hover)",
                  borderColor: "var(--border-strong)",
                  color: "var(--text-primary)",
                }
              : {},
        }}
        aria-label={t("next")}
      >
        ›
      </Box>
    </Box>
  );
}
